import { useEffect, useMemo, useState } from 'react';
import useMenus from '../context/MenuContext';
import masterDataService from '../service/masterData.service';
import { generateAutocompleteItems } from '../utils/helper';
import useUsers from '../context/UsersContext';
import { DataGridIdentifiers } from '../components/Interface';
import { useLoader } from '../provider/LoaderProvider';
import { DocumentDirection } from '../config/DocumentDirection';
import documentTypeService from '../service/newDocumentType.service';
import { CategoryOptionType } from '../config/vatCodes';
import jokerService from '../service/joker.service';
import appService from '../service/app.service';
import useParam from '../context/ParamContext';

const initialAutocompleteData = {
  company: [],
  project: [],
  employee: [],
  item: [],
  qtyUnit: [],
  warehouse: [],
  currency: [],
  paymentMethod: [],
  salesOrPurchasePerson: [],
  costCenter: [],
  deliveryMethod: [],
  incoterm: [],
  priceList: [],
  language: [],
  users: [],
  vatCode: [],
  country: [],
  settlementMode: [],
  customStatus: [],
  documentPiority: [],
};

const initialAutocompleteOptions = {
  company: [],
  country: [],
  customStatus: [],
  documentPiority: [],
  project: [],
  employee: [],
  item: [],
  itemNames: [],
  qtyUnit: [],
  warehouse: [],
  currency: [],
  paymentMethod: [],
  salesOrPurchasePerson: [],
  costCenter: [],
  deliveryMethod: [],
  incoterm: [],
  priceList: [],
  language: [],
  users: [],
  vatCode: [],
  settlementMode: [],
};

export const useDoc = ({ docTypeId }) => {
  const { showLoader, hideLoader, loading } = useLoader();
  const { menus, getMenus, getSubMenuNamesByUrls } = useMenus();
  const { getUser, user } = useUsers();
  const { params, getParam } = useParam();
  const [refetchTable, setRefetchTable] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [documentData, setDocumentData] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [autocompleteOptions, setAutoCompleteOptions] = useState(initialAutocompleteOptions);
  const [autocompleteData, setAutoCompleteData] = useState(initialAutocompleteData);
  const [datagridIdentifiers, setDatagridIdentifiers] = useState({
    doc: DataGridIdentifiers.documentReports,
    docLine: DataGridIdentifiers.docLine,
  });
  const [sourceDatagridIdentifiers, setSourceDatagridIdentifiers] = useState({
    sourceDoc: DataGridIdentifiers.sourceDoc,
    sourceDocLine: DataGridIdentifiers.sourceDocLine,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [upperDocType, setUpperDocType] = useState('');
  const [jokerFields, setJokerFields] = useState([]);
  const [jokerLineFields, setJokerLineFields] = useState([]);

  useEffect(() => {
    if (upperDocType) {
      getJokerFields(upperDocType);
    }
  }, [upperDocType]);

  const getJokerFields = (docType) => {
    jokerService
      .getFields(docType)
      .then((data) => {
        const [headFields, lineFields] = data.reduce(
          (acc, field) => {
            if (field.isRow) {
              acc[1].push(field);
            } else {
              acc[0].push(field);
            }
            return acc;
          },
          [[], []]
        );

        setJokerFields(headFields);
        setJokerLineFields(lineFields);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(params).length) getParam();
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (autocompleteData?.company?.length > 0 && Object.keys(documentData).length > 0) {
      const filteredCompany = autocompleteData.company.filter((c) => {
        if (documentData.cooperation) {
          return c.active && c.cooperation;
        }

        if (documentData.direction === DocumentDirection.INCOMING) {
          return c.active && c.supplier;
        } else if (documentData.direction === DocumentDirection.OUTGOING) {
          return c.active && c.customer;
        }
        return false;
      });
      setAutoCompleteOptions((prev) => ({
        ...prev,
        company: generateAutocompleteItems(filteredCompany, ['code', 'name'], 'id'),
      }));
    }
  }, [autocompleteData.company, documentData]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['docContactMap']);
  }, [menus]);

  const initDefaultValues = () => {
    showLoader();
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        documentTypeService
          .getDocumentType(docTypeId)
          .then((data) => resolve(data))
          .catch((e) => reject(e));
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .quantityUnitToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .warehouseToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .currencyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .paymentMethodToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .salesOrPurchasePersonToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .costCenterToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .deliveryMethodToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .incotermToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priceListToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .languageToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .vatCodeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .countryToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .settlementModeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          company: resp[1],
          project: resp[2],
          employee: resp[3],
          item: [],
          qtyUnit: resp[4],
          warehouse: resp[5],
          currency: resp[6],
          paymentMethod: resp[7],
          salesOrPurchasePerson: resp[8],
          costCenter: resp[9],
          deliveryMethod: resp[10],
          incoterm: resp[11],
          priceList: resp[12],
          language: resp[13],
          users: resp[14],
          vatCode: resp[15],
          country: resp[16],
          settlementMode: resp[17],
        };

        const docData = resp[0];
        setDocumentData(docData);

        const customStatusItems = Array.isArray(docData.customDocStatus) ? docData.customDocStatus : [];
        const activeCustomStatusItems = customStatusItems.filter((item) => item.active);
        const documentPiorityItems = Array.isArray(docData.documentPiority) ? docData.documentPiority : [];
        const activeDocumentPiorityItems = documentPiorityItems.filter((item) => item.active);

        aData.customStatus = customStatusItems;
        aData.documentPiority = documentPiorityItems;
        setAutoCompleteData(aData);

        const filteredCurrency = aData.currency.filter((c) => c.active);
        const filteredPaymentMethod = aData.paymentMethod.filter((p) => !p.deletedBy);
        const filteredSalesOrPurchasePerson = aData.salesOrPurchasePerson.filter((p) => {
          if (docData.direction === DocumentDirection.INCOMING) {
            return p.active && p.purchaser;
          } else if (docData.direction === DocumentDirection.OUTGOING) {
            return p.active && p.salesPerson;
          }
          return false;
        });
        const filteredCostCenter = aData.costCenter.filter((cc) => !cc.deletedBy);

        const filteredVat = aData.vatCode.filter((v) => {
          if (docData.direction === DocumentDirection.INCOMING) {
            return v.active && v.category === CategoryOptionType.B;
          } else if (docData.direction === DocumentDirection.OUTGOING) {
            return v.active && v.category === CategoryOptionType.K;
          }
          return false;
        });

        const acOptions = {
          company: [],
          country: generateAutocompleteItems(aData.country, 'name', 'code'),
          customStatus: generateAutocompleteItems(activeCustomStatusItems, 'name', 'code'),
          documentPiority: generateAutocompleteItems(activeDocumentPiorityItems, 'name', 'priorityId'),
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
          employee: generateAutocompleteItems(aData.employee, 'name', 'id'),
          item: [],
          itemNames: [],
          qtyUnit: generateAutocompleteItems(aData.qtyUnit, 'name', 'id'),
          warehouse: generateAutocompleteItems(aData.warehouse, 'code', 'code'),
          currency: generateAutocompleteItems(filteredCurrency, 'code', 'code'),
          paymentMethod: generateAutocompleteItems(filteredPaymentMethod, 'name', 'id'),
          salesOrPurchasePerson: generateAutocompleteItems(filteredSalesOrPurchasePerson, 'name', 'id'),
          costCenter: generateAutocompleteItems(filteredCostCenter, ['code', 'name'], 'code'),
          deliveryMethod: generateAutocompleteItems(aData.deliveryMethod, 'name', 'id'),
          incoterm: generateAutocompleteItems(aData.incoterm, ['code', 'name'], 'code'),
          priceList: generateAutocompleteItems(aData.priceList, 'name', 'id'),
          language: generateAutocompleteItems(aData.language, ['code', 'name'], 'code'),
          users: generateAutocompleteItems(aData.users, 'userName', 'userId'),
          vatCode: generateAutocompleteItems(filteredVat, 'code', 'code'),
          settlementMode: generateAutocompleteItems(aData.settlementMode, 'name', 'code'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const calculateBalance = (settlement, sumInDocCur) => {
    const docSum = Number(sumInDocCur ?? 0) || 0;
    const totalSettled = Array.isArray(settlement)
      ? settlement.reduce((acc, curr) => acc + (Number(curr?.amount ?? 0) || 0), 0)
      : 0;
    return docSum - totalSettled;
  };

  return {
    refetchTable: refetchTable || false,
    setRefetchTable: setRefetchTable || (() => {}),
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    rowSelectionModel,
    setRowSelectionModel,
    autocompleteOptions,
    setAutoCompleteOptions,
    loading,
    autocompleteData,
    datagridIdentifiers,
    setDatagridIdentifiers,
    sourceDatagridIdentifiers,
    setSourceDatagridIdentifiers,
    submenuNames,
    documentData,
    docTypeId,
    calculateBalance,
    setAutoCompleteData,
    isLoading,
    setIsLoading,
    upperDocType,
    setUpperDocType,
    jokerFields,
    setJokerFields,
    jokerLineFields,
    setJokerLineFields,
  };
};
